/**
 * EspejosLED — Product Configurator
 * Vanilla JS, ES6, no dependencies.
 *
 * Entry point: reads [data-configurator] attribute on the <form> element,
 * parses the embedded JSON config, and initialises the configurator UI.
 *
 * Config JSON shape (see metafield schema):
 * {
 *   template_id: number,
 *   type: "rectangular" | "circular" | "predefined_sizes" | "simple_with_accessories",
 *   base_price: number,
 *   groups: [{ label: string, fields: [{ key, label, type, affects_price, options }] }],
 *   price_table: { "WxH": number },
 *   transport_table: { "WxH": number }
 * }
 */

(function () {
  'use strict';

  var STICKY_BUTTON_SELECTOR = 'sticky-atc-bar button[name="add"]';

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------
  function boot() {
    // Remove any orphaned page-loader overlay left in the DOM by a previous
    // version of this script (bfcache can restore it as visible).
    var orphan = document.getElementById('cfg-page-loader-overlay');
    if (orphan) orphan.remove();
    document.querySelectorAll('.cfg-page-loader').forEach(function (el) { el.remove(); });

    var form = document.querySelector('.configurator-form');
    if (!form) return;

    var dataEl = document.getElementById('configurator-data');
    if (!dataEl) return;

    var config;
    try {
      config = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[Configurator] Failed to parse config JSON:', e);
      return;
    }

    // Load optional fields-info (image + text per field key for ⓘ popovers)
    var fieldsInfo = {};
    var infoEl = document.getElementById('configurator-fields-info');
    if (infoEl) {
      try {
        fieldsInfo = JSON.parse(infoEl.textContent) || {};
      } catch (e) {
        console.warn('[Configurator] Could not parse configurator-fields-info:', e);
      }
    }

    initConfigurator(form, config, fieldsInfo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // ---------------------------------------------------------------------------
  // Initialisation
  // ---------------------------------------------------------------------------
  function initConfigurator(form, config, fieldsInfo) {
    fieldsInfo = fieldsInfo || {};
    var wrapper = form.querySelector('.configurator-wrapper');
    if (!wrapper) return;
    var feedback = form.querySelector('.configurator-feedback');

    form.dataset.configuratorSubmitting = 'false';

    window.addEventListener('pageshow', function () {
      form.dataset.configuratorSubmitting = 'false';
      setButtonLoading(findNativeAddBtn(), false);
      hideFeedback(feedback);
    });

    // Read base price from Shopify variant (native), fallback to metafield
    var basePrice      = parseFloat(form.getAttribute('data-price'))            || parseFloat(config.base_price) || 0;
    var compareAtPrice = parseFloat(form.getAttribute('data-compare-at-price')) || 0;

    // Fixed discount % from Shopify native prices (doesn't change with size)
    var fixedDiscountPct = 0;
    if (compareAtPrice > basePrice && compareAtPrice > 0) {
      fixedDiscountPct = Math.round((compareAtPrice - basePrice) / compareAtPrice * 100);
    }

    // Shared state: last computed total in cents (used by handleAddToCart)
    var lastTotalCents = 0;

    // Hide accelerated-checkout buttons ("Comprar ahora" / Shop Pay / etc.).
    // These bypass the form submit and cannot be intercepted; they must be hidden.
    document.querySelectorAll(
      '.shopify-payment-button, [data-shopify="payment-button"], ' +
      '.dynamic-checkout__content, dynamic-checkout'
    ).forEach(function (el) { el.style.display = 'none'; });

    // Intercept the native Shopify product form submit at window level (capture phase).
    // Modiva uses <product-form is="product-form"> custom element.
    var nativeForm = document.querySelector('product-form form');
    if (nativeForm) {
      window.addEventListener('submit', function (e) {
        if (e.target !== nativeForm) return; // only intercept the native product form
        e.preventDefault();
        e.stopImmediatePropagation();
        var nativeBtn = nativeForm.querySelector('button[name="add"]');
        handleAddToCart(form, config, lastTotalCents, nativeBtn);
      }, true);
    }

    // ---------------------------------------------------------------------------
    // Sticky bar interception — two complementary layers:
    //
    // Layer 1 (primary): Override handleAddToCartClick() on the sticky-add-to-cart
    //   custom element. This is a PUBLIC method that Horizon calls when the sticky
    //   button is clicked. Overriding it on the element instance is stable across
    //   morph() calls because morph only replaces innerHTML, not the element itself.
    //
    // Layer 2 (fallback): pointerdown capture on the sticky bar container.
    //   Unlike 'click', pointerdown fires on a disabled button's parent, so it
    //   catches the case where the button is momentarily disabled.
    // ---------------------------------------------------------------------------
    // Modiva uses <sticky-atc-bar> custom element (no public handleAddToCartClick).
    // Use pointerdown capture on the container to intercept ATC clicks.
    function overrideStickyBar() {
      var stickyEl = document.querySelector('sticky-atc-bar');
      if (!stickyEl || stickyEl._cfgOverridden) return;
      stickyEl._cfgOverridden = true;

      stickyEl.addEventListener('pointerdown', function (e) {
        var btn = e.target.closest('button[name="add"]');
        if (!btn) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        handleAddToCart(form, config, lastTotalCents, btn);
      }, true);
    }

    if (document.querySelector('sticky-atc-bar')) {
      overrideStickyBar();
    } else {
      var stickyWatcher = new MutationObserver(function () {
        if (document.querySelector('sticky-atc-bar')) {
          overrideStickyBar();
          stickyWatcher.disconnect();
        }
      });
      stickyWatcher.observe(document.body, { childList: true, subtree: true });
    }

    // Render all groups + fields
    renderGroups(wrapper, config, fieldsInfo);
    initMeasureSliders(form, config);
    initRalControls(wrapper, fieldsInfo);

    // Bind change events on selects and checkboxes
    wrapper.querySelectorAll('select[data-config-key], input[data-config-key]').forEach(function (el) {
      el.addEventListener('change', function () {
        applyConditions(wrapper, config);
        applyVisibilityRules(wrapper, config);
        updateGroupSummaries(wrapper, config);
        updateMeasurementPreview(form, config);
        lastTotalCents = updatePrice(form, config, basePrice, compareAtPrice, fixedDiscountPct);
      });
    });

    // Initial apply + price calculation
    applyConditions(wrapper, config);
    applyVisibilityRules(wrapper, config);
    updateGroupSummaries(wrapper, config);
    updateMeasurementPreview(form, config);
    lastTotalCents = updatePrice(form, config, basePrice, compareAtPrice, fixedDiscountPct);

    // Bind form submit
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      // e.submitter is the button that triggered the submit (modern browsers).
      // Fallback to the configurator's own ATC button if not available.
      var clickedBtn = e.submitter || form.querySelector('.configurator-add-btn');
      handleAddToCart(form, config, lastTotalCents, clickedBtn);
    });
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------
  function renderGroups(wrapper, config, fieldsInfo) {
    fieldsInfo = fieldsInfo || {};
    var html = '';

    config.groups.forEach(function (group, index) {
      var isAccessoryGroup = isAccessoriesGroup(group);
      var fieldsHtml = group.fields.map(function (field, fieldIndex) {
        return renderField(field, fieldsInfo, {
          isAccessoryGroup: isAccessoryGroup,
          forceOff: isAccessoryGroup && fieldIndex === 0,
          groupLabel: group && group.label ? group.label : ''
        });
      }).join('');

      var isOpen = true; // all groups open by default
      var groupClass = getGroupLayoutClass(group);
      var stepNumber = index + 1;
      var groupIcon = getGroupIconMarkup(group);

      html += (
        '<div class="configurator-group ' + groupClass + (isOpen ? ' is-open' : '') + '">' +
          '<div class="configurator-group__header">' +
            '<span class="configurator-group__step">' + stepNumber + '</span>' +
            '<span class="configurator-group__heading-row">' +
              groupIcon +
              '<span class="configurator-group__title">' + escapeHtml(group.label) + '</span>' +
            '</span>' +
          '</div>' +
          '<div class="configurator-group__body">' +
            '<div class="configurator-group__content">' + fieldsHtml + '</div>' +
          '</div>' +
        '</div>'
      );
    });

    wrapper.innerHTML = html;
    initGallery();
    initPopovers(wrapper, fieldsInfo);
    initAccessoryControls(wrapper);
    initFieldSwatches(wrapper);
    initFieldTiles(wrapper);
  }

  function getGroupLayoutClass(group) {
    var keys = (group.fields || []).map(function (field) { return field.key; });
    var hasMeasureKey = keys.some(function (key) {
      return ['ancho', 'alto', 'ancho1', 'alto1', 'diametro', 'medida', 'medidas'].indexOf(key) !== -1;
    });

    if (hasMeasureKey) return 'configurator-group--measure';
    if (isAccessoriesGroup(group)) return 'configurator-group--accessories configurator-group--stack';
    return 'configurator-group--stack';
  }

  function isAccessoriesGroup(group) {
    return group && typeof group.label === 'string' && group.label.toLowerCase().indexOf('accesorios') !== -1;
  }

  function getGroupIconMarkup(group) {
    if (isAccessoriesGroup(group)) {
      return (
        '<span class="configurator-group__icon" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">' +
            '<circle cx="12" cy="12" r="3.2"/>' +
            '<path d="M19 12a7 7 0 0 0-.08-1l2.05-1.6-2-3.46-2.46.8a7.55 7.55 0 0 0-1.72-1l-.38-2.55h-4l-.38 2.55a7.55 7.55 0 0 0-1.72 1l-2.46-.8-2 3.46L5.08 11a8.6 8.6 0 0 0 0 2l-2.05 1.6 2 3.46 2.46-.8a7.55 7.55 0 0 0 1.72 1l.38 2.55h4l.38-2.55a7.55 7.55 0 0 0 1.72-1l2.46.8 2-3.46L18.92 13c.05-.33.08-.66.08-1z" stroke-linejoin="round"/>' +
          '</svg>' +
        '</span>'
      );
    }

    return (
      '<span class="configurator-group__icon" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">' +
          '<path d="M4 8.5h16v7H4z" stroke-linejoin="round"/>' +
          '<path d="M8 8.5v4m4-4v4m4-4v4" stroke-linecap="round"/>' +
        '</svg>' +
      '</span>'
    );
  }

  // ---------------------------------------------------------------------------
  // Groups stay open. The old accordion header was removed to avoid covering options.
  // ---------------------------------------------------------------------------
  function initAccordions(wrapper) {
    if (!wrapper) return;
  }

  // ---------------------------------------------------------------------------
  // Gallery — thumbnail switching
  // ---------------------------------------------------------------------------
  function initGallery() {
    var mainImg = document.getElementById('cfg-main-img');
    if (!mainImg) return;

    document.querySelectorAll('.cfg-gallery__thumb').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var src = thumb.getAttribute('data-media-src');
        var alt = thumb.getAttribute('data-media-alt');

        // Highlight active thumb
        document.querySelectorAll('.cfg-gallery__thumb').forEach(function (t) {
          t.classList.remove('is-active');
        });
        thumb.classList.add('is-active');

        // Swap main image with fade
        mainImg.classList.add('is-loading');
        var newImg = new Image();
        newImg.onload = function () {
          mainImg.src = src;
          if (alt) mainImg.alt = alt;
          mainImg.classList.remove('is-loading');
        };
        newImg.onerror = function () {
          mainImg.src = src;
          mainImg.classList.remove('is-loading');
        };
        newImg.src = src;
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Info popovers — ⓘ button next to each field label
  // ---------------------------------------------------------------------------
  function initPopovers(wrapper, fieldsInfo) {
    if (!fieldsInfo || !Object.keys(fieldsInfo).length) return;

    // One shared popover element, repositioned on each open
    var popover = document.getElementById('cfg-info-popover');
    if (!popover) {
      popover = document.createElement('div');
      popover.id = 'cfg-info-popover';
      popover.className = 'cfg-info-popover';
      popover.setAttribute('role', 'tooltip');
      popover.setAttribute('aria-hidden', 'true');
      document.body.appendChild(popover);
    }

    var activeBtn = null;

    function openPopover(btn, info) {
      var layoutClass = getEditorialLayoutClass(info, fieldsInfo);
      var headingHtml = info.heading
        ? '<h4 class="cfg-info-popover__title">' + escapeHtml(info.heading) + '</h4>'
        : '';
      var imgHtml = info.image
        ? '<img class="cfg-info-popover__img" src="' + escapeHtml(info.image) + '" alt="" loading="lazy">'
        : '';
      var textHtml = info.text
        ? renderRichText(info.text, 'cfg-info-popover__text')
        : '';

      popover.innerHTML =
        '<button type="button" class="cfg-info-popover__close" aria-label="Cerrar">&times;</button>' +
        (headingHtml || imgHtml || textHtml
          ? '<div class="cfg-info-popover__body' + (imgHtml ? ' cfg-info-popover__body--has-img' : '') + ' ' + layoutClass + '">' +
              imgHtml + '<div class="cfg-info-popover__copy">' + headingHtml + textHtml + '</div>' +
            '</div>'
          : '');

      popover.setAttribute('aria-hidden', 'false');
      popover.classList.add('is-visible');
      btn.setAttribute('aria-expanded', 'true');
      activeBtn = btn;

      positionPopover(btn);

      popover.querySelector('.cfg-info-popover__close').addEventListener('click', closePopover);
    }

    function closePopover() {
      popover.classList.remove('is-visible');
      popover.setAttribute('aria-hidden', 'true');
      if (activeBtn) {
        activeBtn.setAttribute('aria-expanded', 'false');
        activeBtn = null;
      }
    }

    function positionPopover(btn) {
      var rect = btn.getBoundingClientRect();
      var scrollY = window.scrollY || window.pageYOffset;
      var scrollX = window.scrollX || window.pageXOffset;

      // Match width and horizontal position to the configurator column so the
      // tooltip never causes horizontal scroll on narrow viewports.
      var col = btn.closest('.configurator-form') || btn.closest('.configurator-wrapper');
      var top = rect.bottom + scrollY + 8;
      var left, width;
      if (col) {
        var colRect = col.getBoundingClientRect();
        left = colRect.left + scrollX;
        width = colRect.width;
      } else {
        left = rect.left + scrollX;
        width = Math.min(520, window.innerWidth - 32);
        var maxLeft = window.innerWidth - width - 16;
        if (left > maxLeft) left = maxLeft;
        if (left < 8) left = 8;
      }

      popover.style.top   = top + 'px';
      popover.style.left  = left + 'px';
      popover.style.width = width + 'px';
    }

    wrapper.addEventListener('click', function (e) {
      var btn = e.target.closest('.cfg-info-btn');
      if (!btn) return;

      // Toggle: clicking the same button closes
      if (btn === activeBtn) {
        closePopover();
        return;
      }

      var key = btn.getAttribute('data-info-key');
      var info = fieldsInfo[key];
      if (!info) return;

      openPopover(btn, info);
    });

    // Close on outside click
    document.addEventListener('click', function (e) {
      if (!activeBtn) return;
      if (popover.contains(e.target) || e.target.closest('.cfg-info-btn')) return;
      closePopover();
    });

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && activeBtn) closePopover();
    });
  }

  // ---------------------------------------------------------------------------
  // Accordion summaries — update collapsed group header with selected values
  // ---------------------------------------------------------------------------
  function updateGroupSummaries(wrapper, config) {
    var groups = wrapper.querySelectorAll('.configurator-group');
    groups.forEach(function (groupEl, idx) {
      var summaryEl = groupEl.querySelector('.configurator-group__summary');
      if (!summaryEl) return;

      var group = config.groups[idx];
      if (!group) return;

      var parts = [];
      group.fields.forEach(function (field) {
        var el = groupEl.querySelector('[data-config-key="' + field.key + '"]');
        if (!el) return;
        if (el.closest('.configurator-field') && el.closest('.configurator-field').style.display === 'none') return;

        if (el.tagName === 'SELECT') {
          var opt = el.options[el.selectedIndex];
          if (opt && opt.value && opt.value !== '0' && opt.text.toLowerCase() !== 'no') {
            parts.push(opt.text);
          }
        } else if (el.type === 'checkbox' && el.checked) {
          parts.push(field.label);
        }
      });

      summaryEl.textContent = parts.slice(0, 3).join(', ');
    });
  }

  function renderField(field, fieldsInfo, renderOptions) {
    fieldsInfo = fieldsInfo || {};
    renderOptions = renderOptions || {};
    var info = fieldsInfo[field.key];
    var displayLabel = getEditorialHeading(field, info);
    var editorialClass = getEditorialLayoutClass(info, fieldsInfo);
    var isPreviewMeasureField = ['ancho', 'alto', 'ancho1', 'alto1', 'diametro', 'medida', 'medidas'].indexOf(field.key) !== -1;
    var infoBtn = (info && (info.heading || info.text || info.image))
      ? '<button type="button" class="cfg-info-btn" data-info-key="' + escapeHtml(field.key) + '" aria-label="Más información sobre ' + escapeHtml(field.label) + '" tabindex="0">i</button>'
      : '';

    if (field.type === 'switch') {
      var switchDescription = getSwitchDescription(field, info);
      var switchHelp = (switchDescription || (info && info.image))
        ? (
          '<div class="configurator-switch-help">' +
            '<div class="configurator-switch-help__copy">' +
              '<span class="configurator-switch-help__title">' + escapeHtml(displayLabel) + '</span>' +
            (switchDescription ? renderRichText(switchDescription, '') : '') +
            '</div>' +
            ((info && info.image)
              ? '<span class="configurator-switch-help__media"><img src="' + escapeHtml(info.image) + '" alt="" loading="lazy"></span>'
              : '') +
          '</div>'
        )
        : '';

      return (
        '<div class="configurator-field configurator-field--switch ' + editorialClass + '">' +
          '<div class="configurator-field__surface">' +
            '<div class="configurator-switch-row">' +
              '<div class="configurator-switch-main">' + switchHelp + '</div>' +
              '<label class="configurator-switch-label" aria-label="' + escapeHtml(field.label) + '">' +
                '<input type="checkbox" ' +
                  'id="cfg-' + escapeHtml(field.key) + '" ' +
                  'name="' + escapeHtml(field.key) + '" ' +
                  'data-config-key="' + escapeHtml(field.key) + '" ' +
                  'data-affects-price="false" ' +
                  'class="configurator-switch-input"' +
                '>' +
                '<span class="configurator-switch-toggle"></span>' +
              '</label>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }

    if (field.type !== 'dropdown') return '';

    if (renderOptions.isAccessoryGroup && !isPreviewMeasureField) {
      return renderAccessoryField(field, infoBtn, renderOptions.forceOff, info, fieldsInfo);
    }

    if (isColorAccessoryField(field, renderOptions.groupLabel)) {
      return renderColorSwatchField(field, infoBtn, info, fieldsInfo);
    }

    if (!isPreviewMeasureField && Array.isArray(field.options) && field.options.length > 0 && field.options.length <= 8) {
      return renderTileSelectField(field, infoBtn, info, fieldsInfo);
    }

    var fieldDescription = getFieldDescription(field, info);
    var optionsHtml = field.options.map(function (opt) {
      var selected = opt.default ? ' selected' : '';
      return '<option value="' + escapeHtml(opt.value) + '"' + selected + '>'
        + escapeHtml(opt.label) + '</option>';
    }).join('');

    return (
      '<div class="configurator-field ' + editorialClass + (isPreviewMeasureField ? ' configurator-field--hidden-measure' : '') + '">' +
        '<div class="configurator-field__surface">' +
          '<div class="configurator-field__heading">' +
            '<div class="configurator-field__label-row">' +
              '<label for="cfg-' + escapeHtml(field.key) + '">' + escapeHtml(displayLabel) + '</label>' +
              infoBtn +
            '</div>' +
            (fieldDescription ? renderRichText(fieldDescription, 'configurator-field__desc') : '') +
          '</div>' +
          '<select ' +
            'id="cfg-' + escapeHtml(field.key) + '" ' +
            'name="' + escapeHtml(field.key) + '" ' +
            'data-config-key="' + escapeHtml(field.key) + '" ' +
            'data-affects-price="' + (field.affects_price ? 'true' : 'false') + '"' +
          '>' +
          optionsHtml +
          '</select>' +
        '</div>' +
      '</div>'
    );
  }

  function renderTileSelectField(field, infoBtn, info, fieldsInfo) {
    var displayLabel = getEditorialHeading(field, info);
    var editorialClass = getEditorialLayoutClass(info, fieldsInfo);
    var selectedIndex = Math.max(0, field.options.findIndex(function (opt) { return opt.default; }));
    if (selectedIndex < 0) selectedIndex = 0;
    var selectedOption = field.options[selectedIndex] || field.options[0] || {};
    var fieldDescription = getFieldDescription(field, info);
    var optionsHtml = field.options.map(function (opt, optIndex) {
      var selected = optIndex === selectedIndex ? ' selected' : '';
      return '<option value="' + escapeHtml(opt.value) + '"' + selected + '>'
        + escapeHtml(opt.label) + '</option>';
    }).join('');
    var buttonsHtml = field.options.map(function (opt, optIndex) {
      var isSelected = optIndex === selectedIndex;
      return (
        '<button type="button" class="configurator-tile-option' + (isSelected ? ' is-selected' : '') + '" data-field-tile data-index="' + optIndex + '" data-value="' + escapeHtml(opt.value) + '">' +
          '<span class="configurator-tile-option__label">' + escapeHtml(opt.label) + '</span>' +
        '</button>'
      );
    }).join('');

    return (
      '<div class="configurator-field configurator-field--tile-select ' + editorialClass + '">' +
        '<div class="configurator-field__surface">' +
          '<div class="configurator-field__heading configurator-field__heading--stack">' +
            '<div class="configurator-field__label-row">' +
              '<label for="cfg-' + escapeHtml(field.key) + '">' + escapeHtml(displayLabel) + '</label>' +
              infoBtn +
            '</div>' +
            '<p class="configurator-tile-select__current"><span class="configurator-tile-select__current-label">' + escapeHtml(displayLabel) + ':</span> <span data-field-current>' + escapeHtml(selectedOption.label || '') + '</span></p>' +
            (fieldDescription ? renderRichText(fieldDescription, 'configurator-field__desc') : '') +
          '</div>' +
          '<select ' +
            'id="cfg-' + escapeHtml(field.key) + '" ' +
            'name="' + escapeHtml(field.key) + '" ' +
            'data-config-key="' + escapeHtml(field.key) + '" ' +
            'data-affects-price="' + (field.affects_price ? 'true' : 'false') + '" ' +
            'class="configurator-accessory-native"' +
          '>' +
          optionsHtml +
          '</select>' +
          '<div class="configurator-tile-options">' + buttonsHtml + '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function getSwitchDescription(field, info) {
    if (info && info.text) return info.text;
    if (!field || !field.key) return '';

    var normalizedKey = String(field.key).toLowerCase();
    var normalizedLabel = String(field.label || '').toLowerCase();
    if (normalizedKey.indexOf('posibilidad_vertical') !== -1 || normalizedLabel.indexOf('posibilidad vertical') !== -1) {
      return 'Permite instalar el espejo tambien en posicion vertical si lo necesitas.';
    }

    return '';
  }

  function getEditorialHeading(field, info) {
    if (info && info.heading) return info.heading;
    return field && field.label ? field.label : '';
  }

  function getEditorialLayoutClass(info, fieldsInfo) {
    var layout = info && info.layout && info.layout !== 'default'
      ? info.layout
      : fieldsInfo && fieldsInfo._settings && fieldsInfo._settings.layout_default;

    if (layout !== 'columns') layout = 'rows';
    return 'configurator-field--editorial-' + layout;
  }

  function getOptionInfo(field, option, info) {
    if (!field || !option || !info || !info.options) return null;
    var optionId = getOptionEditorialId(field.key, option.label);
    if (!optionId || !info.options[optionId]) return null;
    var optionInfo = info.options[optionId];
    if (!optionInfo.text && !optionInfo.image && !optionInfo.color) return null;
    return optionInfo;
  }

  function getOptionEditorialId(fieldKey, label) {
    var key = String(fieldKey || '').toLowerCase();
    var text = normalizeOptionLabel(label);

    if (key === 'sensor_on_off') {
      if (text.indexOf('tactil') !== -1) return 'sensor_tactil';
      if (text.indexOf('infrarrojo') !== -1) return 'sensor_infrarrojo';
      if (text.indexOf('reloj') !== -1 || text.indexOf('temp') !== -1) return 'sensor_reloj_temp';
      if (text.indexOf('intensidad') !== -1) return 'sensor_cambio_intensidad';
      if (text.indexOf('cambio de luz') !== -1) return 'sensor_cambio_luz';
    }
    if (key === 'lamina_antivaho') {
      if (text.indexOf('con sensor') !== -1) return 'con_sensor';
      if (text.indexOf('sin sensor') !== -1) return 'sin_sensor';
    }
    if (key === 'espejo_aumento') {
      if (text.indexOf('fijacion lateral') !== -1) return 'x3_fijacion_lateral';
      if (text.indexOf('regulable') !== -1 || text.indexOf('imantado') !== -1) return 'x3_regulable_imantado';
      if (text.indexOf('con luz') !== -1 && text.indexOf('sensor') !== -1) return 'con_luz_sensor';
      if (text.indexOf('con luz') !== -1) return 'con_luz';
      if (text.indexOf('iman') !== -1) return 'sin_luz_iman';
      if (text.indexOf('sin luz') !== -1) return 'sin_luz';
    }
    if (key === 'radio_control') {
      if (text.indexOf('meteorologica') !== -1) return 'estacion_meteorologica';
      if (text.indexOf('display') !== -1 || text.indexOf('radio') !== -1) return 'display_radio';
      if (text.indexOf('digital') !== -1) return 'estacion_digital';
      if (text === 'si') return 'si';
    }
    if (key === 'reloj_temperatura' || key === 'altavoces' || key === 'enchufe') {
      if (text === 'si') return 'si';
    }
    if (key === 'tipo_luz' || key === 'tipo_luz_espejo_aumento') {
      if (text.indexOf('calida') !== -1) return 'calida';
      if (text.indexOf('neutra') !== -1) return 'neutra';
      if (text.indexOf('fria') !== -1) return 'fria';
      if (text.indexOf('biled') !== -1 || text.indexOf('3 colores') !== -1) return 'biled_3_colores';
    }
    if (key === 'difusor') {
      if (text.indexOf('sin difusor') !== -1) return 'sin_difusor';
      if (text.indexOf('con difusor') !== -1) return 'con_difusor';
    }
    if (key === 'marco') {
      if (text.indexOf('negro') !== -1 && text.indexOf('mate') !== -1) return 'negro_mate';
      if (text.indexOf('aluminio') !== -1) return 'aluminio';
    }
    if (key === 'pintura_marco') {
      if (text.indexOf('oro rosa') !== -1 || text.indexOf('rosa') !== -1) return 'oro_rosa';
      if (text.indexOf('negro') !== -1) return 'negro';
      if (text.indexOf('dorado') !== -1 || text.indexOf('oro') !== -1) return 'dorado';
      if (text.indexOf('blanco') !== -1) return 'blanco';
    }
    if (key === 'color') {
      if (text.indexOf('paleta ral') !== -1 || text.indexOf('ral') !== -1) return 'paleta_ral';
      if (text.indexOf('oro rosa') !== -1 || text.indexOf('rosa') !== -1) return 'oro_rosa';
      if (text.indexOf('negro') !== -1 && text.indexOf('mate') !== -1) return 'negro_mate';
      if (text.indexOf('blanco') !== -1 && text.indexOf('mate') !== -1) return 'blanco_mate';
      if (text.indexOf('cobre') !== -1 && text.indexOf('mate') !== -1) return 'cobre_mate';
      if (text.indexOf('oro') !== -1 && text.indexOf('mate') !== -1) return 'oro_mate';
      if (text.indexOf('inox') !== -1) return 'inox_mate';
      if (text.indexOf('plata') !== -1) return 'plata_mate';
      if (text.indexOf('bronce') !== -1) return 'bronce_mate';
    }
    if (key === 'color_correa_cuero') {
      if (text.indexOf('negro') !== -1) return 'cuero_negro';
      if (text.indexOf('marron') !== -1 || text.indexOf('marrón') !== -1) return 'cuero_marron';
    }

    return '';
  }

  function normalizeOptionLabel(label) {
    return String(label || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function renderColorSwatchField(field, infoBtn, info, fieldsInfo) {
    var displayLabel = getEditorialHeading(field, info);
    var editorialClass = getEditorialLayoutClass(info, fieldsInfo);
    var ralPdfUrl = fieldsInfo && fieldsInfo._settings && fieldsInfo._settings.ral_pdf_url
      ? fieldsInfo._settings.ral_pdf_url
      : '';
    var selectedIndex = Math.max(0, field.options.findIndex(function (opt) { return opt.default; }));
    var optionsHtml = field.options.map(function (opt, optIndex) {
      var selected = optIndex === selectedIndex ? ' selected' : '';
      return '<option value="' + escapeHtml(opt.value) + '"' + selected + '>'
        + escapeHtml(opt.label) + '</option>';
    }).join('');
    var swatchesHtml = field.options.map(function (opt, optIndex) {
      var isSelected = optIndex === selectedIndex;
      var optionInfo = getOptionInfo(field, opt, info);
      var swatch = getAccessoryColorSwatch(opt, optionInfo);
      return (
        '<button type="button" class="configurator-accessory-swatch' + (isSelected ? ' is-selected' : '') + '" data-field-swatch data-index="' + optIndex + '" data-value="' + escapeHtml(opt.value) + '" aria-label="' + escapeHtml(opt.label) + '" title="' + escapeHtml(opt.label) + '">' +
          '<span class="configurator-accessory-swatch__chip" style="' + escapeHtml(swatch.style) + '">' +
            '<span class="configurator-accessory-swatch__check" aria-hidden="true">' + (swatch.check || '&#10003;') + '</span>' +
          '</span>' +
          '<span class="configurator-accessory-swatch__label">' + escapeHtml(opt.label) + '</span>' +
        '</button>'
      );
    }).join('');

    return (
      '<div class="configurator-field configurator-field--swatch-select ' + editorialClass + '">' +
        '<div class="configurator-field__surface">' +
          '<div class="configurator-field__heading">' +
            '<div class="configurator-field__label-row">' +
              '<label for="cfg-' + escapeHtml(field.key) + '">' + escapeHtml(displayLabel) + '</label>' +
              infoBtn +
            '</div>' +
          '</div>' +
          '<select ' +
            'id="cfg-' + escapeHtml(field.key) + '" ' +
            'name="' + escapeHtml(field.key) + '" ' +
            'data-config-key="' + escapeHtml(field.key) + '" ' +
            'data-affects-price="' + (field.affects_price ? 'true' : 'false') + '" ' +
            'class="configurator-accessory-native"' +
          '>' +
          optionsHtml +
          '</select>' +
          '<div class="configurator-accessory-options configurator-accessory-options--swatches">' +
            swatchesHtml +
          '</div>' +
          (field.key === 'color' ? renderRalInput(ralPdfUrl) : '') +
        '</div>' +
      '</div>'
    );
  }

  function renderRalInput(pdfUrl) {
    var link = pdfUrl
      ? '<a class="configurator-ral__link" href="' + escapeHtml(pdfUrl) + '" target="_blank" rel="noopener">Consultar carta RAL</a>'
      : '<span class="configurator-ral__link configurator-ral__link--muted">Carta RAL pendiente</span>';

    return (
      '<div class="configurator-ral" data-ral-panel hidden>' +
        '<label class="configurator-ral__label" for="cfg-color-ral">Código RAL</label>' +
        '<div class="configurator-ral__row">' +
          '<input id="cfg-color-ral" class="configurator-ral__input" type="text" maxlength="12" placeholder="Ej. RAL 9010" autocomplete="off" data-ral-input>' +
          link +
        '</div>' +
        '<p class="configurator-ral__error" data-ral-error hidden>Introduce el código RAL para continuar.</p>' +
      '</div>'
    );
  }

  function getFieldDescription(field, info) {
    if (info && info.text) return info.text;
    if (!field) return '';

    var key = String(field.key || '').toLowerCase();
    var label = String(field.label || '').toLowerCase();

    if (key.indexOf('led') !== -1 || label.indexOf('led') !== -1) {
      return 'Define la densidad de iluminación para ajustar el rendimiento y el acabado del espejo.';
    }

    return '';
  }

  function renderAccessoryField(field, infoBtn, forceOff, info, fieldsInfo) {
    var displayLabel = getEditorialHeading(field, info);
    var editorialClass = getEditorialLayoutClass(info, fieldsInfo);
    var offOption = getOffOption(field.options);
    var hasOffOption = !!offOption;
    var selectedIndex = getInitialAccessoryIndex(field.options, forceOff);
    var selectedOption = field.options[selectedIndex] || field.options[0] || {};
    var selectedIsOff = hasOffOption && isOffOption(selectedOption);
    var isActive = !selectedIsOff;
    var isSwatchField = isColorAccessoryField(field, '');
    var fieldDescription = getAccessoryFieldDescription(field, info);
    var toggleDisabled = hasOffOption ? '' : ' disabled aria-disabled="true"';
    var optionsHtml = field.options.map(function (opt, optIndex) {
      var selected = optIndex === selectedIndex ? ' selected' : '';
      return '<option value="' + escapeHtml(opt.value) + '"' + selected + '>'
        + escapeHtml(opt.label) + '</option>';
    }).join('');
    var cardsHtml = field.options.map(function (opt, optIndex) {
      if (isOffOption(opt)) return '';
      var isSelected = optIndex === selectedIndex;
      var optionInfo = getOptionInfo(field, opt, info);
      var optionDescription = optionInfo && optionInfo.text
        ? optionInfo.text
        : getAccessoryDescription(field, opt);
      var hasOptionImage = !!(optionInfo && optionInfo.image);
      var hasOptionColor = !!(optionInfo && optionInfo.color);
      var optionImageClass = hasOptionImage ? ' has-custom-image' : (hasOptionColor ? ' has-custom-color' : '');
      var optionMedia = hasOptionImage
        ? '<img src="' + escapeHtml(optionInfo.image) + '" alt="" loading="lazy">'
        : hasOptionColor
          ? '<span class="configurator-accessory-option__color" style="' + escapeHtml(getOptionColorStyle(optionInfo.color)) + '" aria-hidden="true"></span>'
          : (
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">' +
              '<path d="M5 18h14M8 18l1.5-5h5L16 18M9 13V6h6v7M10.5 8.5h3" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>'
          );
      if (isSwatchField) {
        var swatch = getAccessoryColorSwatch(opt, optionInfo);
        return (
          '<button type="button" class="configurator-accessory-swatch' + (isSelected ? ' is-selected' : '') + '" data-accessory-option data-index="' + optIndex + '" data-value="' + escapeHtml(opt.value) + '" aria-label="' + escapeHtml(opt.label) + '" title="' + escapeHtml(opt.label) + '">' +
            '<span class="configurator-accessory-swatch__chip" style="' + escapeHtml(swatch.style) + '">' +
              '<span class="configurator-accessory-swatch__check" aria-hidden="true">' + (swatch.check || '&#10003;') + '</span>' +
            '</span>' +
            '<span class="configurator-accessory-swatch__label">' + escapeHtml(opt.label) + '</span>' +
          '</button>'
        );
      }
      return (
        '<button type="button" class="configurator-accessory-option' + optionImageClass + (isSelected ? ' is-selected' : '') + '" data-accessory-option data-index="' + optIndex + '" data-value="' + escapeHtml(opt.value) + '">' +
          '<span class="configurator-accessory-option__icon" aria-hidden="true">' + getAccessoryOptionIcon(field, opt) + '</span>' +
          '<span class="configurator-accessory-option__copy">' +
            '<span class="configurator-accessory-option__title">' + escapeHtml(opt.label) + '</span>' +
            renderRichText(optionDescription, 'configurator-accessory-option__text') +
          '</span>' +
          '<span class="configurator-accessory-option__media" aria-hidden="true">' +
            optionMedia +
          '</span>' +
        '</button>'
      );
    }).join('');

    return (
        '<div class="configurator-field configurator-field--accessory ' + editorialClass + (isActive ? ' is-active' : '') + (!hasOffOption ? ' is-required' : '') + (isSwatchField ? ' configurator-field--swatches' : '') + '" data-accessory-field>' +
        '<div class="configurator-field__surface">' +
          '<div class="configurator-accessory-head">' +
            '<div class="configurator-accessory-copy">' +
              '<div class="configurator-field__label-row">' +
                '<label for="cfg-' + escapeHtml(field.key) + '">' + escapeHtml(displayLabel) + '</label>' +
                infoBtn +
              '</div>' +
            (fieldDescription ? renderRichText(fieldDescription, 'configurator-accessory-desc') : '') +
            '</div>' +
            '<label class="configurator-accessory-toggle">' +
              '<input type="checkbox" class="configurator-accessory-toggle__input" data-accessory-toggle' + (isActive ? ' checked' : '') + toggleDisabled + '>' +
              '<span class="configurator-accessory-toggle__track"></span>' +
            '</label>' +
          '</div>' +
          '<select ' +
            'id="cfg-' + escapeHtml(field.key) + '" ' +
            'name="' + escapeHtml(field.key) + '" ' +
            'data-config-key="' + escapeHtml(field.key) + '" ' +
            'data-affects-price="' + (field.affects_price ? 'true' : 'false') + '" ' +
            'class="configurator-accessory-native"' +
          '>' +
          optionsHtml +
          '</select>' +
          '<div class="configurator-accessory-options' + (isSwatchField ? ' configurator-accessory-options--swatches' : '') + '" data-accessory-options' + (isActive ? '' : ' hidden') + '>' +
            cardsHtml +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function getInitialAccessoryIndex(options, forceOff) {
    options = options || [];
    var offIndex = options.findIndex(isOffOption);
    if (forceOff && offIndex !== -1) return offIndex;

    var defaultIndex = options.findIndex(function (opt) { return opt.default; });
    if (defaultIndex !== -1) return defaultIndex;

    if (offIndex !== -1) return offIndex;
    return 0;
  }

  function getOffOption(options) {
    return (options || []).find(isOffOption);
  }

  function isOffOption(option) {
    if (!option || typeof option.label !== 'string') return false;
    var label = option.label.trim().toLowerCase();
    return label === 'no' || label === 'sin' || label === 'ninguno';
  }

  function getAccessoryDescription(field, option) {
    var fieldKey = String(field.key || '').toLowerCase();
    var label = (option.label || '').toLowerCase();
    if (fieldKey.indexOf('sensor') !== -1 || label.indexOf('sensor') !== -1) {
      if (label.indexOf('táctil') !== -1 || label.indexOf('tactil') !== -1) return 'Botón táctil integrado.';
      if (label.indexOf('infrarrojo') !== -1) return 'Encendido por proximidad sin contacto físico.';
      if (label.indexOf('reloj') !== -1 || label.indexOf('temp') !== -1) return 'Sensor con reloj y temperatura integrados.';
    }
    if (fieldKey.indexOf('lamina') !== -1 || fieldKey.indexOf('antivaho') !== -1 || label.indexOf('antivaho') !== -1) {
      if (label === 'sí' || label === 'si') return 'Sistema desempañador continuo.';
      if (label.indexOf('sensor') !== -1) return 'Desempañador con activación automática.';
    }
    if (fieldKey.indexOf('aumento') !== -1 || label.indexOf('aumento') !== -1) {
      return 'Zona ampliada para tareas de precisión.';
    }
    if (fieldKey.indexOf('digital') !== -1 || label.indexOf('digital') !== -1) {
      return 'Información visible en el propio espejo.';
    }
    if (fieldKey.indexOf('altavoz') !== -1 || label.indexOf('bluetooth') !== -1) {
      return 'Audio integrado con conexión Bluetooth.';
    }
    if (field.key === 'tipo_luz') {
      if (label.indexOf('cálida') !== -1 || label.indexOf('calida') !== -1) return 'Luz amarilla y acogedora, ideal para ambientes relajantes.';
      if (label.indexOf('neutra') !== -1) return 'Equilibrio natural para baños y rutinas de uso diario.';
      if (label.indexOf('fría') !== -1 || label.indexOf('fria') !== -1) return 'Luz mas blanca y precisa para maxima claridad.';
      return 'Permite alternar tonos de luz segun el momento.';
    }
    return 'Añade esta opcion al espejo y ajusta el precio automaticamente.';
  }

  function getAccessoryFieldDescription(field, info) {
    if (info && info.text) return info.text;
    if (!field) return '';

    var key = String(field.key || '').toLowerCase();
    var label = String(field.label || '').toLowerCase();

    if (isColorAccessoryField(field, '')) {
      return '';
    }

    if (key.indexOf('sensor') !== -1 || label.indexOf('sensor') !== -1) {
      return 'Activa el sistema de encendido por sensor para mostrar las opciones disponibles.';
    }
    if (key.indexOf('lamina') !== -1 || label.indexOf('antivaho') !== -1) {
      return 'Evita que el espejo se empañe tras la ducha para mantener siempre una vision clara.';
    }
    if (key.indexOf('aumento') !== -1 || label.indexOf('aumento') !== -1) {
      return 'Añade una zona de ampliacion para facilitar maquillaje, afeitado o detalles de precision.';
    }
    if (key.indexOf('digital') !== -1 || label.indexOf('digital') !== -1) {
      return 'Integra reloj, temperatura u otras funciones digitales directamente en el espejo.';
    }
    if (key.indexOf('altavoz') !== -1 || label.indexOf('bluetooth') !== -1) {
      return 'Reproduce musica o audio desde el movil con conexion Bluetooth integrada.';
    }
    if (key.indexOf('tipo_luz') !== -1 || label.indexOf('luz') !== -1) {
      return 'Selecciona el tono de iluminacion que mejor encaja con tu baño y tu rutina diaria.';
    }

    return 'Activa esta opcion para ver las variantes disponibles y su impacto en el producto.';
  }

  function isColorAccessoryField(field, groupLabel) {
    if (!field) return false;
    var key = String(field.key || '').toLowerCase();
    var label = String(field.label || '').toLowerCase();
    var group = String(groupLabel || '').toLowerCase();
    var options = Array.isArray(field.options) ? field.options : [];
    var colorOptionCount = options.filter(function (opt) {
      var optionLabel = String(opt && opt.label || '').toLowerCase();
      return optionLabel.indexOf('negro') !== -1 ||
        optionLabel.indexOf('black') !== -1 ||
        optionLabel.indexOf('blanco') !== -1 ||
        optionLabel.indexOf('white') !== -1 ||
        optionLabel.indexOf('dorado') !== -1 ||
        optionLabel.indexOf('oro') !== -1 ||
        optionLabel.indexOf('gold') !== -1 ||
        optionLabel.indexOf('plateado') !== -1 ||
        optionLabel.indexOf('plata') !== -1 ||
        optionLabel.indexOf('silver') !== -1 ||
        optionLabel.indexOf('cobre') !== -1 ||
        optionLabel.indexOf('bronce') !== -1;
    }).length;
    return (key.indexOf('color') !== -1 && key.indexOf('marco') !== -1) ||
      key === 'color' ||
      (label.indexOf('color') !== -1 && label.indexOf('marco') !== -1) ||
      label === 'color' ||
      (label === 'color' && group.indexOf('color del marco') !== -1) ||
      (group.indexOf('color del marco') !== -1) ||
      colorOptionCount >= 2;
  }

  function getAccessoryColorSwatch(option, optionInfo) {
    if (optionInfo && optionInfo.color) {
      return {
        style: getOptionColorStyle(optionInfo.color),
        check: '&#10003;'
      };
    }
    var label = String(option && option.label || '').toLowerCase();
    if (label.indexOf('negro') !== -1 || label.indexOf('black') !== -1) {
      return { style: 'background:#000;border-color:#000;color:#fff;', check: '&#10003;' };
    }
    if (label.indexOf('blanco') !== -1 || label.indexOf('white') !== -1) {
      return { style: 'background:#fff;border-color:#c9cfca;color:#2f3430;', check: '&#10003;' };
    }
    if (label.indexOf('dorado') !== -1 || label.indexOf('oro') !== -1 || label.indexOf('gold') !== -1) {
      return { style: 'background:#D4AF37;border-color:#D4AF37;color:#fff;', check: '&#10003;' };
    }
    if (label.indexOf('plateado') !== -1 || label.indexOf('silver') !== -1 || label.indexOf('plata') !== -1) {
      return { style: 'background:#E5E4E2;border-color:#cfcfcb;color:#2f3430;', check: '&#10003;' };
    }
    return { style: 'background:#f4f4f0;border-color:#cfd4ce;color:#2f3430;', check: '&#10003;' };
  }

  function getOptionColorStyle(color) {
    var colorValue = String(color || '').trim();
    if (!colorValue) return 'background:#f4f4f0;border-color:#cfd4ce;color:#2f3430;';
    return 'background:' + colorValue + ';border-color:' + getVisibleBorderForBackground(colorValue) + ';color:' + getReadableColorForBackground(colorValue) + ';';
  }

  function getVisibleBorderForBackground(color) {
    var luminance = getHexLuminance(color);
    if (luminance === null) return color;
    return luminance > 0.78 ? '#bfc6bf' : color;
  }

  function getReadableColorForBackground(color) {
    var luminance = getHexLuminance(color);
    if (luminance === null) return '#2f3430';
    return luminance > 0.58 ? '#2f3430' : '#fff';
  }

  function getHexLuminance(color) {
    var hex = String(color || '').trim().replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(function (char) { return char + char; }).join('');
    }
    if (!/^[0-9a-f]{6}$/i.test(hex)) return null;
    var r = parseInt(hex.slice(0, 2), 16);
    var g = parseInt(hex.slice(2, 4), 16);
    var b = parseInt(hex.slice(4, 6), 16);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  }

  function getAccessoryOptionIcon(field, option) {
    var fieldKey = String(field.key || '').toLowerCase();
    var label = String(option.label || '').toLowerCase();

    if (fieldKey.indexOf('sensor') !== -1 || label.indexOf('sensor') !== -1) {
      if (label.indexOf('táctil') !== -1 || label.indexOf('tactil') !== -1) {
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 4v8m0 0V8.5a1.75 1.75 0 1 1 3.5 0V13m-3.5-1V7.75a1.75 1.75 0 1 0-3.5 0V14m3.5-2V6.75a1.75 1.75 0 1 1 3.5 0V13m0 0V9.75a1.75 1.75 0 1 1 3.5 0V15a6.5 6.5 0 1 1-13 0v-2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      }
      if (label.indexOf('infrarrojo') !== -1) {
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 9.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Zm-5.5 2.5a5.5 5.5 0 0 1 11 0m-14 0a8.5 8.5 0 0 1 17 0m-14 0a8.5 8.5 0 0 0 17 0" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      }
      if (label.indexOf('reloj') !== -1 || label.indexOf('temp') !== -1) {
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M5 12h4m6 0h4M12 5v4m0 6v4" stroke-linecap="round"/><path d="M9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0Z" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      }
    }

    if (fieldKey.indexOf('lamina') !== -1 || fieldKey.indexOf('antivaho') !== -1 || label.indexOf('antivaho') !== -1) {
      if (label.indexOf('sensor') !== -1) {
        return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 14c2.5 0 2.5-4 5-4s2.5 4 5 4m-10 4c2.5 0 2.5-4 5-4s2.5 4 5 4" stroke-linecap="round"/><path d="M18 8.5a3.5 3.5 0 0 1 0 7m2.2-9.2a6 6 0 0 1 0 11.4" stroke-linecap="round"/></svg>';
      }
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 14c2.5 0 2.5-4 5-4s2.5 4 5 4m-10 4c2.5 0 2.5-4 5-4s2.5 4 5 4" stroke-linecap="round"/><path d="M17 4.5c2 1.2 3 3.1 3 5.5" stroke-linecap="round"/></svg>';
    }

    if (fieldKey.indexOf('altavoz') !== -1 || label.indexOf('bluetooth') !== -1) {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="m10 7 4 5-4 5m4-10-4 5 4 5" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.5 9.5 11 12l-4.5 2.5M17.5 9.5 13 12l4.5 2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }

    if (fieldKey.indexOf('digital') !== -1) {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="5" y="6" width="14" height="12" rx="2"/><path d="M8 10h2m4 0h2m-8 4h8" stroke-linecap="round"/></svg>';
    }

    if (fieldKey.indexOf('aumento') !== -1) {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="11" cy="11" r="5"/><path d="m15 15 4 4M11 9v4M9 11h4" stroke-linecap="round"/></svg>';
    }

    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M5 18h14M8 18l1.5-5h5L16 18M9 13V6h6v7M10.5 8.5h3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  function initAccessoryControls(wrapper) {
    wrapper.querySelectorAll('[data-accessory-field]').forEach(function (fieldEl) {
      var select = fieldEl.querySelector('select[data-config-key]');
      var toggle = fieldEl.querySelector('[data-accessory-toggle]');
      var optionsWrap = fieldEl.querySelector('[data-accessory-options]');
      var buttons = fieldEl.querySelectorAll('[data-accessory-option]');
      if (!select || !toggle || !optionsWrap) return;

      function selectedIsOff() {
        var opt = select.options[select.selectedIndex];
        return opt && isOffOption({ label: opt.text, value: opt.value });
      }

      function firstActiveValue() {
        for (var i = 0; i < select.options.length; i += 1) {
          if (!isOffOption({ label: select.options[i].text, value: select.options[i].value })) {
            return i;
          }
        }
        return select.selectedIndex;
      }

      function sync() {
        var active = !selectedIsOff();
        fieldEl.classList.toggle('is-active', active);
        toggle.checked = active;
        optionsWrap.hidden = !active;
        // Buttons whose data-value matches current select.value (detach-safe).
        var matching = Array.prototype.filter.call(buttons, function (b) {
          return String(b.getAttribute('data-value')) === String(select.value);
        });
        // When several options share the same value (e.g. tipo_luz with all 0s),
        // keep the button that was already is-selected (set by click handler);
        // otherwise default to the first match.
        var winner = null;
        if (matching.length > 1) {
          winner = Array.prototype.find.call(matching, function (b) {
            return b.classList.contains('is-selected');
          }) || matching[0];
        } else if (matching.length === 1) {
          winner = matching[0];
        }
        buttons.forEach(function (button) {
          button.classList.toggle('is-selected', button === winner);
        });
      }

      toggle.addEventListener('change', function () {
        if (toggle.checked) {
          select.selectedIndex = firstActiveValue();
        } else {
          var offIndex = Array.prototype.findIndex.call(select.options, function (option) {
            return isOffOption({ label: option.text, value: option.value });
          });
          if (offIndex !== -1) select.selectedIndex = offIndex;
        }
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          // Pre-mark clicked button so sync() preserves it for duplicate values.
          buttons.forEach(function (b) { b.classList.remove('is-selected'); });
          button.classList.add('is-selected');
          select.selectedIndex = Number(button.dataset.index);
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      select.addEventListener('change', sync);
      sync();
    });
  }

  function initFieldSwatches(wrapper) {
    wrapper.querySelectorAll('.configurator-field--swatch-select').forEach(function (fieldEl) {
      var select = fieldEl.querySelector('select[data-config-key]');
      var buttons = fieldEl.querySelectorAll('[data-field-swatch]');
      if (!select || !buttons.length) return;

      function sync() {
        buttons.forEach(function (button) {
          button.classList.toggle('is-selected', Number(button.dataset.index) === select.selectedIndex);
        });
      }

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          select.selectedIndex = Number(button.dataset.index);
          select.dispatchEvent(new Event('change', { bubbles: true }));
          sync();
        });
      });

      select.addEventListener('change', sync);
      sync();
    });
  }

  function initRalControls(wrapper) {
    var panel = wrapper.querySelector('[data-ral-panel]');
    if (!panel) return;

    var select = wrapper.querySelector('select[data-config-key="color"]');
    var input = panel.querySelector('[data-ral-input]');
    var error = panel.querySelector('[data-ral-error]');
    if (!select || !input) return;

    function sync() {
      var active = isRalSelected(select);
      panel.hidden = !active;
      if (!active) {
        input.value = '';
        input.classList.remove('is-invalid');
        if (error) error.hidden = true;
      }
    }

    select.addEventListener('change', sync);
    input.addEventListener('input', function () {
      input.value = normalizeRalCode(input.value);
      input.classList.remove('is-invalid');
      if (error) error.hidden = true;
    });

    sync();
  }

  function isRalSelected(select) {
    if (!select || select.selectedIndex < 0 || !select.options[select.selectedIndex]) return false;
    var option = select.options[select.selectedIndex];
    var label = normalizeOptionLabel(option.text || option.label || '');
    return label.indexOf('paleta ral') !== -1 || label.indexOf(' ral') !== -1 || label === 'ral';
  }

  function normalizeRalCode(value) {
    return String(value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9\s-]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 12);
  }

  function getRalCode(form) {
    var input = form.querySelector('[data-ral-input]');
    return input ? normalizeRalCode(input.value) : '';
  }

  function validateRalSelection(form) {
    var colorSelect = form.querySelector('select[data-config-key="color"]');
    var input = form.querySelector('[data-ral-input]');
    var error = form.querySelector('[data-ral-error]');
    var requiresRal = isRalSelected(colorSelect);
    var code = getRalCode(form);

    if (!requiresRal || code) {
      if (input) input.classList.remove('is-invalid');
      if (error) error.hidden = true;
      return { ok: true, message: '' };
    }

    if (input) {
      input.classList.add('is-invalid');
      input.focus();
    }
    if (error) error.hidden = false;
    return { ok: false, message: 'Introduce el código RAL antes de añadir el espejo al carrito.' };
  }

  function initFieldTiles(wrapper) {
    wrapper.querySelectorAll('.configurator-field--tile-select').forEach(function (fieldEl) {
      var select = fieldEl.querySelector('select[data-config-key]');
      var buttons = fieldEl.querySelectorAll('[data-field-tile]');
      var current = fieldEl.querySelector('[data-field-current]');
      if (!select || !buttons.length) return;

      function sync() {
        buttons.forEach(function (button) {
          button.classList.toggle('is-selected', Number(button.dataset.index) === select.selectedIndex);
        });
        if (current && select.options[select.selectedIndex]) {
          current.textContent = select.options[select.selectedIndex].text;
        }
      }

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          select.selectedIndex = Number(button.dataset.index);
          select.dispatchEvent(new Event('change', { bubbles: true }));
          sync();
        });
      });

      select.addEventListener('change', sync);
      sync();
    });
  }

  // ---------------------------------------------------------------------------
  // Condition evaluation — show/hide fields based on current selections
  // ---------------------------------------------------------------------------

  /**
   * Evaluate a single 'when' clause against the current selection map.
   * Supported ops: eq, neq, lt, gt, lte, gte
   */
  function evalClause(clause, selected) {
    var sel = selected[clause.field];
    if (sel === undefined || sel === null) return false;

    var selNum = parseFloat(sel);
    var valNum = parseFloat(clause.value);

    switch (clause.op) {
      case 'eq':  return sel === clause.value || (!isNaN(selNum) && !isNaN(valNum) && selNum === valNum);
      case 'neq': return sel !== clause.value && (isNaN(selNum) || isNaN(valNum) || selNum !== valNum);
      case 'lt':  return !isNaN(selNum) && !isNaN(valNum) && selNum < valNum;
      case 'gt':  return !isNaN(selNum) && !isNaN(valNum) && selNum > valNum;
      case 'lte': return !isNaN(selNum) && !isNaN(valNum) && selNum <= valNum;
      case 'gte': return !isNaN(selNum) && !isNaN(valNum) && selNum >= valNum;
      default:    return false;
    }
  }

  /**
   * Apply all visibility conditions from config.conditions.
   * Called after every field change and on initial render.
   *
   * Each condition:
   *   { when: [{field, op, value}, …], logic: "or"|"and", action: "hide"|"show", target: "field_key" }
   *
   * "hide" action: if condition is TRUE → hide the field wrapper + reset its select to default.
   * Multiple conditions may target the same field; if ANY hides it, it stays hidden.
   */
  function applyConditions(wrapper, config) {
    var conditions = config.conditions;
    if (!conditions || !conditions.length) return;

    var selected = {};
    wrapper.querySelectorAll('select[data-config-key]').forEach(function (sel) {
      selected[sel.getAttribute('data-config-key')] = sel.value;
    });
    wrapper.querySelectorAll('input[type="checkbox"][data-config-key]').forEach(function (cb) {
      selected[cb.getAttribute('data-config-key')] = cb.checked ? '1' : '0';
    });

    // Collect hide verdicts per target field key (true = must be hidden)
    var hideMap = {};

    conditions.forEach(function (cond) {
      var clauses = cond.when;
      var logic   = cond.logic || 'or';
      var matched;

      if (logic === 'and') {
        matched = clauses.every(function (c) { return evalClause(c, selected); });
      } else {
        matched = clauses.some(function (c) { return evalClause(c, selected); });
      }

      var target = cond.target;
      if (cond.action === 'hide') {
        // Once any condition marks a field hidden, it stays hidden
        if (matched) hideMap[target] = true;
        else if (hideMap[target] === undefined) hideMap[target] = false;
      }
      // "show" actions (rare) could be added here in the future
    });

    // Apply hide/show to each target field's DOM wrapper
    Object.keys(hideMap).forEach(function (fieldKey) {
      var el = wrapper.querySelector('[data-config-key="' + fieldKey + '"]');
      if (!el) return;
      var fieldWrap = el.closest('.configurator-field');
      if (!fieldWrap) return;

      if (hideMap[fieldKey]) {
        fieldWrap.style.display = 'none';
        // Reset the hidden select to its first option (value "0" or first option)
        // so it doesn't silently add price/properties to the cart
        if (el.tagName === 'SELECT') {
          el.value = el.options[0] ? el.options[0].value : '0';
        } else if (el.type === 'checkbox') {
          el.checked = false;
        }
      } else {
        fieldWrap.style.display = '';
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Visibility rules (FASE 3+ — replica del PS DP module via dp-evaluator.js)
  //
  // config.visibility_rules: [{ when: "[campo] = valor", hide_fields: [...],
  //                              hide_options: [{field, value}] }]
  // Si el metafield no trae visibility_rules o DPEvaluator no está cargado,
  // esta función es no-op (productos no migrados siguen funcionando con
  // applyConditions legacy).
  // ---------------------------------------------------------------------------
  // Predefined-sizes products store the size as a single string "WxH" in `medida`
  // but PS DP intervals reference [ancho] and [alto] separately. Derive them.
  function enrichStateWithSize(state, config) {
    if (!config) return state;
    if (config.type === 'predefined_sizes') {
      var medida = state.medida || state.medidas || '';
      var m = String(medida).match(/^(\d+)\s*[xX×]\s*(\d+)/);
      if (m) {
        if (state.ancho === undefined) state.ancho = m[1];
        if (state.alto  === undefined) state.alto  = m[2];
      }
    } else if (config.type === 'circular') {
      // Some intervals on circular products may reference [ancho] as a synonym
      // for [diametro]. Mirror it.
      if (state.diametro && state.ancho === undefined) state.ancho = state.diametro;
    }
    return state;
  }

  function applyVisibilityRules(wrapper, config) {
    if (!config.visibility_rules || !window.DPEvaluator) return;

    // Collect state from current selections
    var state = {};
    wrapper.querySelectorAll('select[data-config-key]').forEach(function (sel) {
      state[sel.getAttribute('data-config-key')] = sel.value;
    });
    wrapper.querySelectorAll('input[type="checkbox"][data-config-key]').forEach(function (cb) {
      state[cb.getAttribute('data-config-key')] = cb.checked ? '1' : '0';
    });
    wrapper.querySelectorAll('input[type="number"][data-config-key], input[type="range"][data-config-key]').forEach(function (inp) {
      state[inp.getAttribute('data-config-key')] = inp.value;
    });
    enrichStateWithSize(state, config);

    var vis = window.DPEvaluator.applyVisibility(
      config.visibility_rules, state, config.base_prices, config.intervals
    );

    // 1. Reset previous markers from this function. For <option> elements we
    //    physically detach/reattach to be reliable across browsers (style.display
    //    on <option> is not universally honored by Safari/Firefox).
    //
    //    Restore detached options (stored in select's _detachedOptions) first.
    wrapper.querySelectorAll('select[data-config-key]').forEach(function (sel) {
      if (sel._detachedOptions && sel._detachedOptions.length) {
        sel._detachedOptions.forEach(function (item) {
          // Re-insert at original index, clamped
          var idx = Math.min(item.index, sel.options.length);
          if (idx >= sel.options.length) sel.appendChild(item.option);
          else sel.insertBefore(item.option, sel.options[idx]);
          item.option.removeAttribute('data-vis-rule-hidden');
        });
        sel._detachedOptions = [];
      }
    });
    // Reset accessory-toggle lock applied by previous pass
    wrapper.querySelectorAll('[data-accessory-field][data-vis-rule-locked="1"]').forEach(function (fieldEl) {
      var toggleInput = fieldEl.querySelector('.configurator-accessory-toggle__input');
      if (toggleInput) {
        toggleInput.disabled = false;
        toggleInput.removeAttribute('aria-disabled');
      }
      fieldEl.classList.remove('is-required');
      fieldEl.removeAttribute('data-vis-rule-locked');
    });
    wrapper.querySelectorAll('[data-vis-rule-hidden="1"]').forEach(function (el) {
      if (el.tagName === 'BUTTON') {
        el.disabled = false;
        el.removeAttribute('aria-disabled');
        el.style.opacity = '';
        el.style.pointerEvents = '';
        el.style.filter = '';
      } else if (el.tagName !== 'OPTION') {
        // Field wrap
        el.style.display = '';
      }
      el.removeAttribute('data-vis-rule-hidden');
    });

    // 2. Hide whole fields
    vis.hiddenFields.forEach(function (key) {
      var el = wrapper.querySelector('[data-config-key="' + key + '"]');
      if (!el) return;
      var fieldWrap = el.closest('.configurator-field');
      if (!fieldWrap) return;
      fieldWrap.style.display = 'none';
      fieldWrap.setAttribute('data-vis-rule-hidden', '1');
      // Reset value so it doesn't silently affect price/cart
      if (el.tagName === 'SELECT' && el.options[0]) el.value = el.options[0].value;
      else if (el.type === 'checkbox') el.checked = false;
    });

    // 3. Hide individual dropdown options + auto-reset if selected became hidden.
    //    Also visually disable card-style buttons (data-field-tile, data-field-swatch,
    //    data-accessory-option) so the user gets feedback that they can't pick them.
    Object.keys(vis.hiddenOptions).forEach(function (fieldKey) {
      var sel = wrapper.querySelector('select[data-config-key="' + fieldKey + '"]');
      if (!sel) return;
      var hiddenSet = vis.hiddenOptions[fieldKey];
      var fieldWrap = sel.closest('.configurator-field');

      // Detach native <option>s physically (cross-browser safe).
      if (!sel._detachedOptions) sel._detachedOptions = [];
      var optsList = Array.prototype.slice.call(sel.options);
      optsList.forEach(function (opt, idx) {
        if (hiddenSet.has(String(opt.value))) {
          opt.setAttribute('data-vis-rule-hidden', '1');
          sel._detachedOptions.push({ index: idx, option: opt });
          if (opt.parentNode) opt.parentNode.removeChild(opt);
        }
      });

      // Disable card-style buttons that share the same value
      if (fieldWrap) {
        var btnSel = '[data-field-tile][data-value], [data-field-swatch][data-value], [data-accessory-option][data-value]';
        fieldWrap.querySelectorAll(btnSel).forEach(function (btn) {
          if (hiddenSet.has(String(btn.getAttribute('data-value')))) {
            btn.disabled = true;
            btn.setAttribute('aria-disabled', 'true');
            btn.style.opacity = '0.4';
            btn.style.pointerEvents = 'none';
            btn.style.filter = 'grayscale(50%)';
            btn.setAttribute('data-vis-rule-hidden', '1');
          }
        });
      }

      // Auto-reset if currently-selected value was hidden
      if (hiddenSet.has(String(sel.value))) {
        var firstVisible = Array.prototype.slice.call(sel.options).find(function (o) {
          return !o.disabled;
        });
        if (firstVisible) {
          sel.value = firstVisible.value;
          // Sync card-button selection state
          if (fieldWrap) {
            fieldWrap.querySelectorAll('[data-field-tile], [data-field-swatch], [data-accessory-option]').forEach(function (b) {
              var matches = String(b.getAttribute('data-value')) === String(firstVisible.value);
              b.classList.toggle('is-selected', matches);
            });
          }
        }
        // No dispatchEvent — caller will run updatePrice next, picking up the new state.
      }

      // Sync accessory-field UI: when the rule forced a non-off value, the
      // accessory's own change-listener is not fired (we deliberately skip
      // dispatchEvent above to avoid a feedback loop). Mirror sync() manually so
      // the toggle, fila visible y botón seleccionado reflejan el valor real.
      // Además, si la opción "off" del campo está oculta por reglas, bloquea el
      // toggle (no se puede apagar) y marca la fila como requerida.
      if (fieldWrap && fieldWrap.hasAttribute('data-accessory-field')) {
        var accSelected = sel.options[sel.selectedIndex];
        var accIsOff = accSelected && isOffOption({ label: accSelected.text, value: accSelected.value });
        var accToggle = fieldWrap.querySelector('.configurator-accessory-toggle__input');
        var accOptionsWrap = fieldWrap.querySelector('[data-accessory-options]');
        var offHidden = Array.prototype.some.call(sel.options, function (o) {
          return isOffOption({ label: o.text, value: o.value }) && hiddenSet.has(String(o.value));
        });

        fieldWrap.classList.toggle('is-active', !accIsOff);
        if (accToggle) accToggle.checked = !accIsOff;
        if (accOptionsWrap) accOptionsWrap.hidden = accIsOff;
        fieldWrap.querySelectorAll('[data-accessory-option]').forEach(function (b) {
          // Compare by value, not by index — detached options shift selectedIndex.
          b.classList.toggle('is-selected', String(b.getAttribute('data-value')) === String(sel.value));
        });

        if (offHidden && accToggle) {
          accToggle.disabled = true;
          accToggle.setAttribute('aria-disabled', 'true');
          fieldWrap.classList.add('is-required');
          fieldWrap.setAttribute('data-vis-rule-locked', '1');
        }
      }
    });
  }


  // ---------------------------------------------------------------------------
  // Price calculation
  // ---------------------------------------------------------------------------
  function getSelectedValues(form) {
    var values = {};
    var selects = form.querySelectorAll('select[data-config-key]');
    selects.forEach(function (sel) {
      values[sel.getAttribute('data-config-key')] = sel.value;
    });
    var colorSelect = form.querySelector('select[data-config-key="color"]');
    var ralCode = getRalCode(form);
    if (isRalSelected(colorSelect) && ralCode) {
      values.color_ral = ralCode;
    }
    return values;
  }

  function parseMeasureNumber(value) {
    if (value === null || value === undefined) return 0;
    var match = String(value).replace(',', '.').match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : 0;
  }

  function getPreviewDimensions(selected, config) {
    var type = config.type;
    var width = 0;
    var height = 0;
    var shape = 'rect';
    var label = '';

    if (type === 'rectangular') {
      width = parseMeasureNumber(selected.ancho || selected.ancho1);
      height = parseMeasureNumber(selected.alto || selected.alto1);
      label = width && height ? Math.round(width) + ' x ' + Math.round(height) + ' cm' : '';
      return { width: width, height: height, label: label, shape: 'rect' };
    }

    if (type === 'circular') {
      width = parseMeasureNumber(selected.diametro);
      label = width ? '\u00d8 ' + Math.round(width) + ' cm' : '';
      return { width: width, height: width, label: label, shape: 'circle' };
    }

    if (type === 'predefined_sizes') {
      var sizeKey = selected.medida || selected.medidas || '';
      var nums = parsePredefinedSizeNumbers(sizeKey);
      if (nums.length >= 2) {
        width = parseMeasureNumber(nums[0]);
        height = parseMeasureNumber(nums[1]);
        shape = 'oval';
        label = Math.round(width) + ' x ' + Math.round(height) + ' cm';
      } else if (nums.length === 1) {
        width = parseMeasureNumber(nums[0]);
        height = width;
        shape = 'circle';
        label = '\u00d8 ' + Math.round(width) + ' cm';
      }
      return { width: width, height: height, label: label, shape: shape, controlLabel: label };
    }

    return { width: width, height: height, label: label, shape: shape };
  }

  function parsePredefinedSizeNumbers(sizeKey) {
    var raw = String(sizeKey || '').trim();
    var nums = raw.match(/\d+(?:[.,]\d+)?/g) || [];
    if (nums.length !== 1) return nums;

    var compact = nums[0].replace(/\D/g, '');
    if (!/^\d{4,5}$/.test(compact)) return nums;

    var candidates = [];
    if (compact.length >= 4) candidates.push([compact.slice(0, 2), compact.slice(2)]);
    if (compact.length === 5) candidates.push([compact.slice(0, 3), compact.slice(3)]);

    var valid = candidates.find(function (pair) {
      var width = parseInt(pair[0], 10);
      var height = parseInt(pair[1], 10);
      return width >= 30 && width <= 220 && height >= 30 && height <= 220;
    });

    return valid || nums;
  }

  function findMeasureSelect(form, keys) {
    for (var i = 0; i < keys.length; i += 1) {
      var select = form.querySelector('select[data-config-key="' + keys[i] + '"]');
      if (select) return select;
    }
    return null;
  }

  function getMeasureMeta(select) {
    if (!select) return null;
    var options = Array.prototype.slice.call(select.options || []).map(function (option, index) {
      return {
        index: index,
        value: option.value,
        label: option.text
      };
    }).filter(function (option) {
      return option.value !== '';
    });
    return options.length ? options : null;
  }

  function getPredefinedSizeOptions(select) {
    if (!select) return [];
    return Array.prototype.slice.call(select.options || []).map(function (option, index) {
      var nums = parsePredefinedSizeNumbers(option.value || option.text);
      if (nums.length < 2) nums = parsePredefinedSizeNumbers(option.text || option.value);
      if (nums.length < 2) return null;
      return {
        index: index,
        value: option.value,
        label: option.text,
        width: parseMeasureNumber(nums[0]),
        height: parseMeasureNumber(nums[1])
      };
    }).filter(function (option) {
      return option && option.width > 0 && option.height > 0;
    });
  }

  function uniqueSortedNumbers(values) {
    var seen = {};
    return values.filter(function (value) {
      var key = String(value);
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    }).sort(function (a, b) { return a - b; });
  }

  function getNearestIndex(values, value) {
    if (!values.length) return 0;
    var nearest = 0;
    var nearestDistance = Infinity;
    values.forEach(function (candidate, index) {
      var distance = Math.abs(candidate - value);
      if (distance < nearestDistance) {
        nearest = index;
        nearestDistance = distance;
      }
    });
    return nearest;
  }

  function buildNumericTickMarkup(values, reverseOrder) {
    if (!values.length) return '';
    var labels = values.map(function (value) { return Math.round(value) + ' cm'; });
    if (reverseOrder) labels.reverse();
    return labels.map(function (label) {
      return '<span>' + escapeHtml(label) + '</span>';
    }).join('');
  }

  function buildTickMarkup(options, reverseOrder, formatter) {
    if (!options || !options.length) return '';

    var desiredIndexes = [0, Math.floor((options.length - 1) / 2), options.length - 1];
    if (options.length >= 4) {
      desiredIndexes = [0, Math.floor((options.length - 1) / 3), Math.floor((options.length - 1) * 2 / 3), options.length - 1];
    }

    var seen = {};
    var labels = desiredIndexes.map(function (idx) { return options[idx]; }).filter(function (option) {
      if (!option) return false;
      if (seen[option.index]) return false;
      seen[option.index] = true;
      return true;
    });

    if (reverseOrder) labels.reverse();

    return labels.map(function (option) {
      var label = typeof formatter === 'function' ? formatter(option) : option.label;
      return '<span>' + escapeHtml(label) + '</span>';
    }).join('');
  }

  function setupMeasureSlider(control, select, reverseTicks, tickFormatter) {
    if (!control || !select) {
      if (control) control.hidden = true;
      return;
    }

    var slider = control.querySelector('input[type="range"]');
    var ticks = control.querySelector('[data-preview-height-ticks], [data-preview-width-ticks]');
    var options = getMeasureMeta(select);
    if (!slider || !options) {
      control.hidden = true;
      return;
    }

    control.hidden = false;
    slider.min = 0;
    slider.max = String(options.length - 1);
    slider.step = 1;

    if (ticks) ticks.innerHTML = buildTickMarkup(options, reverseTicks, tickFormatter);

    function syncFromSelect() {
      slider.value = String(Math.max(0, select.selectedIndex));
    }

    slider.addEventListener('input', function () {
      var index = parseInt(slider.value, 10);
      if (isNaN(index) || !select.options[index]) return;
      if (select.selectedIndex === index) return;
      select.selectedIndex = index;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });

    select.addEventListener('change', syncFromSelect);
    syncFromSelect();
  }

  function setupPredefinedSizeSliders(widthControl, heightControl, select) {
    var options = getPredefinedSizeOptions(select);
    if (!select || !options.length) {
      if (widthControl) widthControl.hidden = true;
      if (heightControl) heightControl.hidden = true;
      return;
    }

    var widths = uniqueSortedNumbers(options.map(function (option) { return option.width; }));
    var heights = uniqueSortedNumbers(options.map(function (option) { return option.height; }));
    var widthSlider = widthControl && widthControl.querySelector('input[type="range"]');
    var heightSlider = heightControl && heightControl.querySelector('input[type="range"]');
    var widthTicks = widthControl && widthControl.querySelector('[data-preview-width-ticks]');
    var heightTicks = heightControl && heightControl.querySelector('[data-preview-height-ticks]');

    if (!widthSlider || !heightSlider) {
      if (widthControl) widthControl.hidden = true;
      if (heightControl) heightControl.hidden = true;
      return;
    }

    widthControl.hidden = false;
    heightControl.hidden = false;
    widthSlider.min = 0;
    widthSlider.max = String(widths.length - 1);
    widthSlider.step = 1;
    heightSlider.min = 0;
    heightSlider.max = String(heights.length - 1);
    heightSlider.step = 1;

    if (widthTicks) widthTicks.innerHTML = buildNumericTickMarkup(widths, false);
    if (heightTicks) heightTicks.innerHTML = buildNumericTickMarkup(heights, true);

    function currentOption() {
      return options.find(function (option) { return option.index === select.selectedIndex; }) || options[0];
    }

    function syncFromSelect() {
      var option = currentOption();
      widthSlider.value = String(getNearestIndex(widths, option.width));
      heightSlider.value = String(getNearestIndex(heights, option.height));
    }

    function chooseNearest(targetWidth, targetHeight, priority) {
      var exactAxisMatches = options.filter(function (option) {
        return priority === 'width' ? option.width === targetWidth : option.height === targetHeight;
      });
      var pool = exactAxisMatches.length ? exactAxisMatches : options;
      var best = pool[0];
      var bestScore = Infinity;
      pool.forEach(function (option) {
        var widthWeight = priority === 'width' ? 0.55 : 1;
        var heightWeight = priority === 'height' ? 0.55 : 1;
        var score = Math.abs(option.width - targetWidth) * widthWeight + Math.abs(option.height - targetHeight) * heightWeight;
        if (score < bestScore) {
          best = option;
          bestScore = score;
        }
      });
      if (best && select.selectedIndex !== best.index) {
        select.selectedIndex = best.index;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        syncFromSelect();
      }
    }

    widthSlider.addEventListener('input', function () {
      var current = currentOption();
      var targetWidth = widths[parseInt(widthSlider.value, 10)] || current.width;
      chooseNearest(targetWidth, current.height, 'width');
    });

    heightSlider.addEventListener('input', function () {
      var current = currentOption();
      var targetHeight = heights[parseInt(heightSlider.value, 10)] || current.height;
      chooseNearest(current.width, targetHeight, 'height');
    });

    select.addEventListener('change', syncFromSelect);
    syncFromSelect();
  }

  function setMeasureControlLabel(control, label) {
    if (!control) return;
    var labelEl = control.querySelector('.cfg-measure-control__label');
    if (labelEl) labelEl.textContent = label;
  }

  function getNumericSelectRange(select) {
    if (!select) return null;
    var values = Array.prototype.slice.call(select.options || []).map(function (option) {
      return parseMeasureNumber(option.value || option.text);
    }).filter(function (value) {
      return value > 0;
    });
    if (!values.length) return null;
    return {
      min: Math.min.apply(null, values),
      max: Math.max.apply(null, values)
    };
  }

  function initMeasureSliders(form, config) {
    var type = config.type;
    var widthSelect = null;
    var heightSelect = null;
    var widthControl = form.querySelector('[data-preview-width-control]');
    var heightControl = form.querySelector('[data-preview-height-control]');

    if (type === 'circular') {
      widthSelect = findMeasureSelect(form, ['diametro']);
      setMeasureControlLabel(widthControl, 'Diámetro (cm)');
      if (heightControl) heightControl.hidden = true;
    } else if (type === 'predefined_sizes') {
      widthSelect = findMeasureSelect(form, ['medida', 'medidas']);
      setMeasureControlLabel(widthControl, 'Ancho (cm)');
      setMeasureControlLabel(heightControl, 'Alto (cm)');
      setupPredefinedSizeSliders(widthControl, heightControl, widthSelect);
      return;
    } else {
      widthSelect = findMeasureSelect(form, ['ancho', 'ancho1']);
      heightSelect = findMeasureSelect(form, ['alto', 'alto1']);
      setMeasureControlLabel(widthControl, 'Ancho (cm)');
      setMeasureControlLabel(heightControl, 'Alto (cm)');
    }

    setupMeasureSlider(widthControl, widthSelect, false);
    setupMeasureSlider(heightControl, heightSelect, true);
  }

  function updateMeasurementPreview(form, config) {
    var preview = form.querySelector('[data-configurator-preview]');
    if (!preview) return;

    var selectionEl = preview.querySelector('[data-preview-selection]');
    var shapeEl = preview.querySelector('[data-preview-shape]');
    var shapeLabelEl = preview.querySelector('[data-preview-shape-label]');
    var widthEl = preview.querySelector('[data-preview-width]');
    var heightEl = preview.querySelector('[data-preview-height]');
    var stageEl = preview.querySelector('[data-preview-stage]');
    if (!selectionEl || !shapeEl || !widthEl || !heightEl) return;

    var selected = getSelectedValues(form);
    var dims = getPreviewDimensions(selected, config);
    var width = dims.width || 0;
    var height = dims.height || 0;
    var diameterSelect = findMeasureSelect(form, ['diametro']);

    if (!width || !height) {
      selectionEl.textContent = 'Selecciona una medida';
      widthEl.textContent = '\u2014';
      heightEl.textContent = '\u2014';
      if (shapeLabelEl) shapeLabelEl.textContent = '';
      shapeEl.style.width = '160px';
      shapeEl.style.height = '160px';
      shapeEl.classList.remove('is-circle', 'is-oval');
      if (stageEl) stageEl.classList.remove('is-circle-mode');
      return;
    }

    var maxWidth = 250;
    var maxHeight = 280;
    var scale = Math.min(maxWidth / width, maxHeight / height);
    var renderWidth = Math.max(54, Math.round(width * scale));
    var renderHeight = Math.max(72, Math.round(height * scale));
    if (dims.shape === 'circle') {
      var range = getNumericSelectRange(diameterSelect);
      var minSize = 118;
      var maxSize = 230;
      var ratio = range && range.max > range.min ? (width - range.min) / (range.max - range.min) : 1;
      ratio = Math.max(0, Math.min(1, ratio));
      var circleSize = Math.round(minSize + (maxSize - minSize) * ratio);
      renderWidth = circleSize;
      renderHeight = circleSize;
    }

    selectionEl.textContent = dims.label || (Math.round(width) + ' x ' + Math.round(height) + ' cm');
    widthEl.textContent = dims.controlLabel || Math.round(width) + ' cm';
    heightEl.textContent = Math.round(height) + ' cm';
    if (shapeLabelEl) {
      shapeLabelEl.textContent = dims.shape === 'circle'
        ? '\u00d8 ' + Math.round(width)
        : Math.round(width) + ' x ' + Math.round(height);
    }
    shapeEl.style.width = renderWidth + 'px';
    shapeEl.style.height = renderHeight + 'px';
    shapeEl.classList.toggle('is-circle', dims.shape === 'circle');
    shapeEl.classList.toggle('is-oval', dims.shape === 'oval');
    if (stageEl) stageEl.classList.toggle('is-circle-mode', dims.shape === 'circle');
  }

  // Returns total in cents (integer) — stored by initConfigurator for cart use
  function updatePrice(form, config, basePrice, compareAtPrice, fixedDiscountPct) {
    var priceEl = form.querySelector('[data-configurator-price]');
    if (!priceEl) {
      priceEl = document.querySelector('[data-configurator-price]');
    }

    var selected       = getSelectedValues(form);
    var type           = config.type;
    var priceTable     = config.price_table     || {};
    var transportTable = config.transport_table || {};

    var sizeKey           = '';
    var priceIncrement    = 0;
    var transportIncrement = 0;
    var accessoryTotal    = 0;

    if (type === 'rectangular') {
      var ancho = selected['ancho'] || selected['ancho1'] || '';
      var alto  = selected['alto']  || selected['alto1']  || '';
      if (ancho && alto) sizeKey = ancho + 'x' + alto;
    } else if (type === 'circular') {
      var diam = selected['diametro'] || '';
      if (diam) sizeKey = diam;
    } else if (type === 'predefined_sizes') {
      sizeKey = selected['medida'] || selected['medidas'] || '';
    }

    priceIncrement     = parseFloat(priceTable[sizeKey])     || 0;
    transportIncrement = parseFloat(transportTable[sizeKey]) || 0;

    // price_factor = IVA (1.21) × (1 - descuento); increments are stored sin IVA
    var priceFactor = parseFloat(config.price_factor) || 1.0285;

    if (config.equation && window.DPEvaluator) {
      // FASE 3+ path: producto con metafield regenerado a partir de la auditoría
      // Dynamic Product. Replica fórmulas e intervals 1:1.
      var evalState = {};
      Object.keys(selected).forEach(function (k) { evalState[k] = selected[k]; });
      enrichStateWithSize(evalState, config);
      var evalResult = window.DPEvaluator.evaluateAccessory({
        equation:   config.equation,
        intervals:  config.intervals   || [],
        basePrices: config.base_prices || {},
        state:      evalState,
      });
      accessoryTotal = evalResult.total;
    } else {
      // Legacy path (productos no migrados todavía a equation/intervals).
      var fieldMap = {};
      (config.groups || []).forEach(function (g) {
        (g.fields || []).forEach(function (f) { fieldMap[f.key] = f; });
      });

      var sizeKeys = new Set(['ancho', 'alto', 'ancho1', 'alto1', 'diametro', 'medida', 'medidas']);
      form.querySelectorAll('select[data-config-key]').forEach(function (sel) {
        var key          = sel.getAttribute('data-config-key');
        var affectsPrice = sel.getAttribute('data-affects-price') === 'true';
        if (sizeKeys.has(key)) return;

        var value = sel.value;
        var field = fieldMap[key];

        if (affectsPrice) {
          var selOpt = field && field.options && field.options.find(function (o) {
            return String(o.value) === String(value);
          });
          var priceVal = (selOpt && selOpt.price !== undefined)
            ? parseFloat(selOpt.price)
            : parseFloat(value);
          if (!isNaN(priceVal) && priceVal > 0) accessoryTotal += priceVal;
          return;
        }

        if (field && field.size_price_table && value && value !== '0') {
          var depTable = config[field.size_price_table] || {};
          var depPrice = parseFloat(depTable[sizeKey] || 0);
          if (!isNaN(depPrice) && depPrice > 0) accessoryTotal += depPrice;
          return;
        }

        if (field && field.soporte_price_table && value && value !== '0') {
          var sTable   = config[field.soporte_price_table] || {};
          var colorSop = selected['color_soporte'] || '0';
          var sPrice   = parseFloat(((sTable[value] || {})[colorSop]) || 0);
          if (!isNaN(sPrice) && sPrice > 0) accessoryTotal += sPrice;
        }
      });
    }

    var incrementsTotal = (priceIncrement + transportIncrement + accessoryTotal) * priceFactor;
    var total = basePrice + incrementsTotal;

    var priceElOriginal = form.querySelector('[data-configurator-price-original]');
    if (priceElOriginal) {
      priceElOriginal.style.display = 'none';
    }

    priceEl.textContent = formatPrice(total);

    // Sync the native Shopify theme price block (top of product page)
    // compareAtPrice here is used only to decide if tachado layout is shown;
    // the displayed compare price scales proportionally with the increment
    var displayCompare = 0;
    if (compareAtPrice > basePrice && compareAtPrice > 0) {
      // Scale compare_at by the same increment so tachado price stays coherent
      var increment = total - basePrice;
      displayCompare = compareAtPrice + increment;
    }
    syncThemePrice(total, displayCompare, fixedDiscountPct);

    return Math.round(total * 100); // cents
  }

  function syncThemePrice(total, originalPrice, discountPct) {
    // Modiva uses plain .f-price divs (no custom element).
    // Structure:
    //   .f-price[.f-price--on-sale]
    //     .f-price__regular > .f-price-item--regular   (regular price text)
    //     .f-price__sale    > .f-price-item--sale       (sale price)
    //                       > .f-price-item--regular s  (compare-at, struck through)
    var allPriceEls = document.querySelectorAll('.f-price');
    if (!allPriceEls.length) return;

    allPriceEls.forEach(function (priceEl) {
      if (priceEl.closest('dialog')) return;

      var regularPriceEl = priceEl.querySelector('.f-price__regular .f-price-item--regular');
      var salePriceEl    = priceEl.querySelector('.f-price__sale .f-price-item--sale');
      var comparePriceEl = priceEl.querySelector('.f-price__sale .f-price-item--regular s');

      if (!regularPriceEl && !salePriceEl) return;

      if (originalPrice > 0) {
        priceEl.classList.add('f-price--on-sale');
        if (salePriceEl)    salePriceEl.textContent    = formatPrice(total);
        if (comparePriceEl) comparePriceEl.textContent = formatPrice(originalPrice);
        var badge = priceEl.querySelector('[data-discount-badge]');
        if (badge && discountPct > 0) {
          badge.textContent = '-' + discountPct + '%';
          badge.style.display = '';
        }
      } else {
        priceEl.classList.remove('f-price--on-sale');
        if (regularPriceEl) regularPriceEl.textContent = formatPrice(total);
        var badge = priceEl.querySelector('[data-discount-badge]');
        if (badge) badge.style.display = 'none';
      }
    });
  }

  function formatPrice(amount) {
    // Spanish locale formatting: decimal comma, thousands dot
    var fixed = amount.toFixed(2);
    var parts = fixed.split('.');
    var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return '\u20ac' + intPart + ',' + parts[1];
  }

  function findMatchingEventTarget(event, selector) {
    if (!event) return null;

    var target = event.target;
    if (target && typeof target.closest === 'function') {
      var directMatch = target.closest(selector);
      if (directMatch) return directMatch;
    }

    if (typeof event.composedPath !== 'function') return null;

    var path = event.composedPath();
    for (var i = 0; i < path.length; i += 1) {
      var node = path[i];
      if (node && typeof node.matches === 'function' && node.matches(selector)) {
        return node;
      }
    }

    return null;
  }

  // ---------------------------------------------------------------------------
  // Cart integration
  // ---------------------------------------------------------------------------

  // URL del endpoint vía App Proxy de Shopify.
  // El proxy redirige /apps/configurador/* → servidor Railway.
  var VARIANT_ENDPOINT = '/apps/configurador/api/variant';
  var CART_RETURN_TO = '/cart';

  function handleAddToCart(form, config, totalCents, clickedBtn) {
    // Apply spinner to the button the user actually clicked (top configurator
    // button or sticky-bar button). Fallback to findNativeAddBtn() if no
    // explicit caller, e.g. legacy calls.
    var btn = clickedBtn || findNativeAddBtn();
    var feedback = form.querySelector('.configurator-feedback');
    var productId = form.getAttribute('data-product-id');
    var variantId = form.getAttribute('data-variant-id');

    if (form.dataset.configuratorSubmitting === 'true') {
      return;
    }

    if (!productId || !variantId) {
      showFeedback(feedback, 'error', 'No se pudo identificar el producto. Recarga la página.');
      return;
    }

    if (!totalCents || totalCents <= 0) {
      showFeedback(feedback, 'error', 'Selecciona las medidas para ver el precio antes de añadir al carrito.');
      setButtonLoading(btn, false);
      return;
    }

    var ralValidation = validateRalSelection(form);
    if (!ralValidation.ok) {
      showFeedback(feedback, 'error', ralValidation.message);
      setButtonLoading(btn, false);
      return;
    }

    var selections = getSelectedValues(form);
    var properties = buildProperties(form, config, totalCents);

    form.dataset.configuratorSubmitting = 'true';
    setButtonLoading(btn, true);
    hideFeedback(feedback);

    // Paso 1: pedir al backend la variante shadow con el precio correcto.
    // Con reintentos automáticos para Vercel cold-starts (5xx) y errores de red.
    var VARIANT_MAX_RETRIES = 3;
    var VARIANT_RETRY_DELAYS = [600, 1500, 3000];

    function fetchVariantWithRetry(retriesLeft) {
      return fetch(VARIANT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId:  productId,
          variantId:  variantId,
          selections: selections,
          totalCents: totalCents
        })
      })
      .then(function (res) {
        // Retry on 5xx (server / cold start) or 408 (timeout)
        if ((res.status >= 500 || res.status === 408) && retriesLeft > 0) {
          var attempt = VARIANT_MAX_RETRIES - retriesLeft;
          var delay = VARIANT_RETRY_DELAYS[attempt] || 3000;
          console.log('[Configurator] Backend ' + res.status + ', reintentando en ' + delay + 'ms (' + retriesLeft + ' restantes)');
          return new Promise(function (resolve) {
            setTimeout(function () { resolve(fetchVariantWithRetry(retriesLeft - 1)); }, delay);
          });
        }
        if (!res.ok) {
          return res.json().then(function (d) {
            throw new Error(d.error || 'Error al procesar la configuración');
          });
        }
        return res.json();
      }, function (networkErr) {
        // Network error (offline, DNS, etc.) → retry
        if (retriesLeft > 0) {
          var attempt = VARIANT_MAX_RETRIES - retriesLeft;
          var delay = VARIANT_RETRY_DELAYS[attempt] || 3000;
          console.log('[Configurator] Network error, reintentando en ' + delay + 'ms (' + retriesLeft + ' restantes)');
          return new Promise(function (resolve) {
            setTimeout(function () { resolve(fetchVariantWithRetry(retriesLeft - 1)); }, delay);
          });
        }
        throw networkErr;
      });
    }

    fetchVariantWithRetry(VARIANT_MAX_RETRIES)
    .then(function (data) {
      if (!data || !data.variantId) {
        throw new Error('No se pudo preparar la variante configurada.');
      }

      // Paso 2: añadir al carrito via AJAX con reintentos automáticos.
      submitShadowVariantToCart(data.variantId, properties, getDesiredQuantity(), form, btn);
    })
    .catch(function (err) {
      console.error('[Configurator] Error:', err);
      form.dataset.configuratorSubmitting = 'false';
      showFeedback(feedback, 'error', err.message || 'No se pudo añadir al carrito. Inténtalo de nuevo.');
      setButtonLoading(btn, false);
    });
  }

  function buildProperties(form, config, totalCents) {
    var properties = {};
    var sizeKeys = new Set(['ancho', 'alto', 'ancho1', 'alto1', 'diametro', 'medida', 'medidas']);

    // Fields used as condition in intervals: their option `value` is a PS ID,
    // NOT a euro amount. The real price comes from interval formulas. So we
    // must NOT append "(+€X)" to their cart label (e.g. difusor value="1"
    // would print "+€1,00" but the real cost is calculated via intervals).
    var intervalConditionFields = new Set();
    (config.intervals || []).forEach(function (iv) {
      (iv.when || []).forEach(function (w) {
        if (w.field) intervalConditionFields.add(w.field);
      });
    });

    // Nota: _precio_cents ya no se incluye — el precio real está en la variante shadow.

    // Flatten all fields from config groups for quick lookup
    var fieldMap = {};
    config.groups.forEach(function (group) {
      group.fields.forEach(function (field) {
        fieldMap[field.key] = field;
      });
    });

    // Dropdowns
    form.querySelectorAll('select[data-config-key]').forEach(function (sel) {
      var key = sel.getAttribute('data-config-key');
      var value = sel.value;
      var field = fieldMap[key];
      if (!field) return;

      // Skip fields currently hidden by a visibility condition (their value
      // defaults to the first option, which may not be 'No' — e.g. tipo_luz_espejo_aumento
      // defaults to 'Cálida'). Without this check, hidden-but-not-deactivated
      // fields would leak into cart properties.
      var wrap = sel.closest('.configurator-field');
      if (wrap && wrap.style.display === 'none') return;

      // Resolve selected option preferring selectedIndex (handles duplicate values
      // like tipo_luz where 3 options share value="0"); fall back to find-by-value
      // when the index is shifted by detached <option>s.
      var idx = sel.selectedIndex;
      var selectedOpt = (idx >= 0 && field.options[idx]) ? field.options[idx] : null;
      if (!selectedOpt || String(selectedOpt.value) !== String(value)) {
        selectedOpt = field.options.find(function (o) { return String(o.value) === String(value); });
      }
      var label = selectedOpt ? selectedOpt.label : value;

      var lowerLabel = label.toLowerCase().trim();
      if (lowerLabel === 'no' || value === '' || value === null) return;

      var propKey = field.label;

      if (field.affects_price && !sizeKeys.has(key) && !intervalConditionFields.has(key)) {
        var priceVal = (selectedOpt && selectedOpt.price !== undefined)
          ? parseFloat(selectedOpt.price)
          : parseFloat(value);
        if (!isNaN(priceVal) && priceVal > 0) {
          label = label + ' (+' + formatPrice(priceVal) + ')';
        }
      }

      if (sizeKeys.has(key) && !label.toLowerCase().includes('cm')) {
        label = label + ' cm';
      }

      properties[propKey] = label;
    });

    var colorSelect = form.querySelector('select[data-config-key="color"]');
    var ralCode = getRalCode(form);
    if (isRalSelected(colorSelect) && ralCode) {
      properties['Color RAL'] = ralCode;
    }

    // Switches (checkboxes)
    form.querySelectorAll('input[type="checkbox"][data-config-key]').forEach(function (cb) {
      if (!cb.checked) return;
      var key = cb.getAttribute('data-config-key');
      var field = fieldMap[key];
      if (!field) return;
      var wrap = cb.closest('.configurator-field');
      if (wrap && wrap.style.display === 'none') return;
      properties[field.label] = 'Sí';
    });

    // Add total price as a property for order clarity
    var priceEl = form.querySelector('[data-configurator-price]');
    if (priceEl && priceEl.textContent && priceEl.textContent !== '\u2014') {
      properties['Precio configurado'] = priceEl.textContent;
    }

    // Hidden property: original product URL so cart link goes back to the configurator
    var productHandle = form.getAttribute('data-product-handle');
    if (productHandle) {
      properties['_producto_url'] = '/products/' + productHandle;
    }

    // Hidden property: base product SKU for supplier order processing
    var baseSku = form.getAttribute('data-sku');
    if (baseSku) {
      properties['_Referencia'] = baseSku;
    }

    return properties;
  }

  function getDesiredQuantity() {
    var quantityInput = document.querySelector(
      '.configurator-form input[name="quantity"], ' +
      'product-form-component form input[name="quantity"], ' +
      'sticky-add-to-cart input[name="quantity"]'
    );

    var quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
    return !isNaN(quantity) && quantity > 0 ? quantity : 1;
  }

  function submitShadowVariantToCart(variantId, properties, quantity, form, clickedBtn) {
    // Reduced retries to avoid triggering Shopify's /cart/add.js rate limit
    // (which locks the IP for 30 min). 2 retries with longer delays is enough
    // for typical CDN propagation lag without flooding the endpoint.
    var MAX_RETRIES = 2;
    var RETRY_DELAY = 2500;

    var payload = {
      items: [{
        id:         Number(variantId),
        quantity:   quantity || 1,
        properties: properties || {}
      }]
    };

    function attempt(retriesLeft) {
      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload)
      })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, status: res.status, data: data };
        });
      })
      .then(function (result) {
        if (result.ok) {
          window.location.href = CART_RETURN_TO || '/cart';
          return;
        }

        var bodyStr = JSON.stringify(result.data).toLowerCase();

        // 429 = rate limit. Retrying makes it WORSE. Show clear message and stop.
        if (result.status === 429 || bodyStr.includes('30 minut') || bodyStr.includes('intenta de nuevo')) {
          throw new Error('Demasiadas peticiones. Por favor espera unos minutos antes de volver a intentarlo.');
        }

        // Retry: out-of-stock (CDN propagation), variant not found (recién creada),
        // 422/404/5xx en general — suelen ser transitorios al añadir variante shadow
        var isTransient =
          (result.status === 422 || result.status === 404 || result.status >= 500) &&
          (bodyStr.includes('agotado') || bodyStr.includes('sold out') ||
           bodyStr.includes('unavailable') || bodyStr.includes('no se encontr') ||
           bodyStr.includes('not found') || bodyStr.includes('does not exist') ||
           bodyStr.includes('invalid'));

        if (isTransient && retriesLeft > 0) {
          console.log('[Configurator] Error transitorio (' + result.status + '), reintentando... (' + retriesLeft + ' restantes)');
          setTimeout(function () { attempt(retriesLeft - 1); }, RETRY_DELAY);
          return;
        }

        var msg = result.data.description || result.data.message || 'No se pudo añadir al carrito.';
        throw new Error(msg);
      })
      .catch(function (err) {
        if (form) {
          form.dataset.configuratorSubmitting = 'false';
          var btn      = clickedBtn || findNativeAddBtn();
          var feedback = form.querySelector('.configurator-feedback');
          setButtonLoading(btn, false);
          showFeedback(feedback, 'error', err.message || 'No se pudo añadir al carrito. Inténtalo de nuevo.');
        }
      });
    }

    attempt(MAX_RETRIES);
  }

  function appendHiddenInput(form, name, value) {
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value == null ? '' : String(value);
    form.appendChild(input);
  }

  // ---------------------------------------------------------------------------
  // UI helpers
  // ---------------------------------------------------------------------------
  // Returns Canyon's native add-to-cart button (outside our configurator form)
  function findNativeAddBtn() {
    var candidates = document.querySelectorAll('button[name="add"]');
    for (var i = 0; i < candidates.length; i++) {
      var btn = candidates[i];
      // Skip any button that might be inside our own configurator form
      if (btn.closest('.configurator-form')) continue;
      return btn;
    }
    return null;
  }

  function setButtonLoading(btn, loading) {
    if (!btn) return;
    if (loading) {
      btn.disabled = true;
      btn.dataset.originalHtml = btn.innerHTML;
      // Inject spinner into Canyon's text span if present, else replace innerHTML
      var textSpan = btn.querySelector('.add-to-cart-text__content');
      if (textSpan) {
        textSpan.dataset.originalHtml = textSpan.innerHTML;
        textSpan.innerHTML = '<span class="btn-spinner"></span>A\u00f1adiendo\u2026';
      } else {
        btn.innerHTML = '<span class="btn-spinner"></span>A\u00f1adiendo\u2026';
      }
    } else {
      btn.disabled = false;
      var textSpan = btn.querySelector('.add-to-cart-text__content');
      if (textSpan && textSpan.dataset.originalHtml) {
        textSpan.innerHTML = textSpan.dataset.originalHtml;
        delete textSpan.dataset.originalHtml;
      } else if (btn.dataset.originalHtml) {
        btn.innerHTML = btn.dataset.originalHtml;
      }
      delete btn.dataset.originalHtml;
    }
  }

  function showFeedback(el, type, message) {
    if (!el) return;
    el.className = 'configurator-feedback configurator-feedback--' + type;
    el.textContent = message;
    el.style.display = 'block';
  }

  function hideFeedback(el) {
    if (!el) return;
    el.style.display = 'none';
  }

  function renderRichText(value, className) {
    if (value === null || value === undefined || value === '') return '';
    var content = String(value);
    var attrs = className ? ' class="' + escapeHtml(className) + '"' : '';

    if (/<[a-z][\s\S]*>/i.test(content)) {
      return '<div' + attrs + '>' + content + '</div>';
    }

    return '<p' + attrs + '>' + escapeHtml(content).replace(/\n/g, '<br>') + '</p>';
  }

  function escapeHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

})();

